<script setup>
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Badge from '@/components/ds/Badge.vue';
import ContextHelp from '@/components/ds/ContextHelp.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { ANOMALIES_EMPTY } from '@/utils/emptyStateCopy.js';

// Detección de anomalías: picos de error/rage/dead por page_path, con
// severidad, observed vs baseline y sparkline. Slice 2 añade snooze/ack.

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const anomalies = ref([]);
const snoozedCount = ref(0);
const sensitivity = ref('default');
const sensitivityBusy = ref(false);
const busy = ref(new Set()); // fingerprints con acción en vuelo
// Drill-down: fingerprint expandido + cache de samples { [fp]: {loading, error, samples} }.
const expanded = ref(null);
const details = ref({});

const SENSITIVITY_OPTIONS = ['conservative', 'default', 'aggressive'];

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.anomalies({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    anomalies.value = result.anomalies ?? [];
    snoozedCount.value = result.snoozedCount ?? 0;
    if (result.sensitivity) sensitivity.value = result.sensitivity;
    ps.setState(anomalies.value.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load anomalies",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// CSRF double-submit: token de la cookie XSRF-TOKEN (patrón de las
// vistas admin). Inline para leer como el resto del código.
function readCsrfToken() {
  const match = typeof document !== 'undefined'
    ? document.cookie.split('; ').find((r) => r.startsWith('XSRF-TOKEN='))
    : null;
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

async function runAction(a, kind) {
  if (!activeAppId.value || busy.value.has(a.fingerprint)) return;
  busy.value = new Set(busy.value).add(a.fingerprint);
  try {
    const csrf = readCsrfToken();
    const args = { appId: activeAppId.value, fingerprint: a.fingerprint, csrf };
    if (kind === 'snooze') await api.events.snoozeAnomaly({ ...args, hours: 24 });
    else await api.events.ackAnomaly(args);
    await tick(); // refresca: snoozed desaparece, acked se marca
  } catch (err) {
    ps.setState('error', { message: `Couldn't ${kind} the anomaly`, detail: err?.message ?? '' });
  } finally {
    const next = new Set(busy.value);
    next.delete(a.fingerprint);
    busy.value = next;
  }
}

async function toggleDetail(a) {
  if (expanded.value === a.fingerprint) { expanded.value = null; return; }
  expanded.value = a.fingerprint;
  if (details.value[a.fingerprint]?.samples || !activeAppId.value) return;
  details.value = { ...details.value, [a.fingerprint]: { loading: true, error: false, samples: [] } };
  try {
    const resp = await api.events.anomalyDetail({
      appId: activeAppId.value, metric: a.metric, pagePath: a.pagePath, window: timeWindow.value,
    });
    details.value = { ...details.value, [a.fingerprint]: { loading: false, error: false, samples: resp?.samples ?? [] } };
  } catch {
    details.value = { ...details.value, [a.fingerprint]: { loading: false, error: true, samples: [] } };
  }
}

async function changeSensitivity(value) {
  if (!activeAppId.value || value === sensitivity.value || sensitivityBusy.value) return;
  sensitivityBusy.value = true;
  const prev = sensitivity.value;
  sensitivity.value = value; // optimista
  try {
    await api.events.setAnomalySensitivity({ appId: activeAppId.value, sensitivity: value, csrf: readCsrfToken() });
    await tick(); // re-detecta con la nueva sensibilidad
  } catch (err) {
    sensitivity.value = prev; // revertir
    ps.setState('error', { message: "Couldn't change sensitivity", detail: err?.message ?? '' });
  } finally {
    sensitivityBusy.value = false;
  }
}

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

const SEVERITY_TONE = Object.freeze({
  critical: 'danger',
  major: 'warning',
  minor: 'accent',
});

const METRIC_LABEL = Object.freeze({
  error: 'Errors',
  rage: 'Rage clicks',
  dead: 'Dead clicks',
});

function metricLabel(m) {
  return METRIC_LABEL[m] ?? m;
}

function fmt(n) {
  return (n ?? 0).toLocaleString('en-US');
}
</script>

<template>
  <PanelCard
    title="Anomalies"
    help-key="anomalies"
    :state="ps.state.value"
    :empty-headline="ANOMALIES_EMPTY.headline"
    :empty-body="ANOMALIES_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <template #header>
      <div class="anomalies__sensitivity">
        <span class="anomalies__sensitivity-label">
          <label for="anomaly-sensitivity">Sensitivity</label>
          <ContextHelp help-key="sensitivity" />
        </span>
        <select
          id="anomaly-sensitivity"
          class="anomalies__sensitivity-select"
          :value="sensitivity"
          :disabled="sensitivityBusy"
          aria-label="Detection sensitivity"
          @change="changeSensitivity($event.target.value)"
        >
          <option
            v-for="opt in SENSITIVITY_OPTIONS"
            :key="opt"
            :value="opt"
          >
            {{ opt }}
          </option>
        </select>
      </div>
    </template>
    <ul class="anomalies">
      <li
        v-for="(a, i) in anomalies"
        :key="`${a.metric}-${a.pagePath}-${i}`"
        class="anomalies__item"
      >
        <div class="anomalies__row">
          <Badge
            :tone="SEVERITY_TONE[a.severity] ?? 'neutral'"
            class="anomalies__sev"
          >
            {{ a.severity }}
          </Badge>
          <div class="anomalies__desc">
            <span class="anomalies__metric">{{ metricLabel(a.metric) }}</span>
            <span
              class="anomalies__page"
              :title="a.pagePath"
            >{{ a.pagePath }}</span>
          </div>
          <span class="anomalies__nums">
            <strong>{{ fmt(a.observed) }}</strong>
            <span class="anomalies__baseline">vs {{ fmt(a.baseline) }} baseline</span>
          </span>
          <AnalyticsChart
            type="line"
            :labels="a.sparkline.map((p) => p.ts)"
            :datasets="[{ label: metricLabel(a.metric), data: a.sparkline.map((p) => p.value) }]"
            :aria-label="`${metricLabel(a.metric)} on ${a.pagePath} — signal over the window`"
            class="anomalies__spark"
          />
          <div class="anomalies__actions">
            <button
              type="button"
              class="anomalies__btn"
              :aria-expanded="expanded === a.fingerprint"
              @click="toggleDetail(a)"
            >
              {{ expanded === a.fingerprint ? 'Hide' : 'Details' }}
            </button>
            <span
              v-if="a.acked"
              class="anomalies__acked"
              :title="a.ackedBy ? `Acknowledged by ${a.ackedBy}` : 'Acknowledged'"
            >Acked</span>
            <button
              v-else
              type="button"
              class="anomalies__btn"
              :disabled="busy.has(a.fingerprint)"
              @click="runAction(a, 'ack')"
            >
              Ack
            </button>
            <button
              type="button"
              class="anomalies__btn"
              :disabled="busy.has(a.fingerprint)"
              @click="runAction(a, 'snooze')"
            >
              Snooze 24h
            </button>
          </div>
        </div>
        <div
          v-if="expanded === a.fingerprint"
          class="anomalies__detail"
        >
          <p
            v-if="details[a.fingerprint]?.loading"
            class="anomalies__detail-note"
          >
            Loading recent events…
          </p>
          <p
            v-else-if="details[a.fingerprint]?.error"
            class="anomalies__detail-note"
          >
            Couldn't load recent events.
          </p>
          <p
            v-else-if="!details[a.fingerprint]?.samples?.length"
            class="anomalies__detail-note"
          >
            No recent events for this page.
          </p>
          <ul
            v-else
            class="anomalies__samples"
          >
            <li
              v-for="(s, si) in details[a.fingerprint].samples"
              :key="si"
              class="anomalies__sample"
            >
              <span class="anomalies__sample-primary">{{ s.primary }}</span>
              <span
                v-if="s.secondary"
                class="anomalies__sample-secondary"
              >{{ s.secondary }}</span>
            </li>
          </ul>
        </div>
      </li>
    </ul>
    <p
      v-if="snoozedCount > 0"
      class="anomalies__snoozed-note"
    >
      {{ snoozedCount }} snoozed {{ snoozedCount === 1 ? 'anomaly' : 'anomalies' }} hidden.
    </p>
    <template
      v-if="anomalies.length"
      #tableFallback
    >
      <table class="an-table">
        <caption class="visually-hidden">
          Detected anomalies in this window
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Severity
            </th>
            <th scope="col">
              Signal
            </th>
            <th scope="col">
              Page
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Observed
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Baseline
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(a, i) in anomalies"
            :key="`t-${a.metric}-${a.pagePath}-${i}`"
          >
            <td>{{ a.severity }}</td>
            <td>{{ metricLabel(a.metric) }}</td>
            <td>{{ a.pagePath }}</td>
            <td class="numeric">
              {{ a.observed }}
            </td>
            <td class="numeric">
              {{ a.baseline }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.anomalies {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.anomalies__item {
  border-radius: var(--radius-sm);
}

.anomalies__row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.anomalies__row:hover {
  background: var(--bg-subtle);
}

.anomalies__detail {
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
}

.anomalies__detail-note {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.anomalies__samples {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.anomalies__sample {
  display: flex;
  flex-direction: column;
  padding: var(--space-1) var(--space-2);
  border-left: var(--bw-thick) solid var(--border-default);
}

.anomalies__sample-primary {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--fg-primary);
  overflow-wrap: anywhere;
}

.anomalies__sample-secondary {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.anomalies__sev {
  text-transform: capitalize;
  flex-shrink: 0;
}

.anomalies__desc {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.anomalies__metric {
  font-size: var(--text-sm);
  color: var(--fg-primary);
}

.anomalies__page {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.anomalies__nums {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-feature-settings: "tnum";
  flex-shrink: 0;
}

.anomalies__baseline {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.anomalies__spark {
  color: var(--chart-2);
  flex-shrink: 0;
}

.anomalies__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.anomalies__btn {
  font: inherit;
  font-size: var(--text-xs);
  padding: var(--space-1) var(--space-2);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-canvas);
  color: var(--fg-secondary);
  cursor: pointer;
}


.anomalies__btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.anomalies__btn:focus-visible {
  outline: var(--bw-thick) solid var(--accent-default);
  outline-offset: var(--space-1);
}

.anomalies__btn:hover:not(:disabled) {
  background: var(--bg-subtle);
  color: var(--fg-primary);
}


.anomalies__acked {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
  font-weight: var(--fw-medium);
}

.anomalies__snoozed-note {
  margin: var(--space-2) 0 0;
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.anomalies__sensitivity {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.anomalies__sensitivity-label {
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.anomalies__sensitivity-select {
  font: inherit;
  font-size: var(--text-xs);
  text-transform: capitalize;
  padding: var(--space-1) var(--space-2);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  cursor: pointer;
}

.anomalies__sensitivity-select:disabled {
  opacity: 0.5;
  cursor: default;
}

.an-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.an-table th,
.an-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.an-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.an-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
